import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
    Watch,
} from '@stencil/core';

import {
    ICON_MAP,
    PortalToastType,
    ToastAction,
} from '../../models/toast';
import store from '../../store/store';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-toast',
    styleUrl: 'portal-toast.scss',
    shadow: true,
})
export class PortalToast {
    @Element() $el: HTMLPortalToastElement;

    /**
   * Type of the toast to set icon internally
   */
    @Prop() type: PortalToastType = PortalToastType.None;

    /**
   * Message of the toat
   */
    // eslint-disable-next-line @stencil/strict-mutable
    @Prop({ mutable: true }) message: string;

    /**
   * Determines if toast is open
   */
    @Prop({ mutable: true }) isopen: boolean;

    /**
   * Actions that have to appear in toat
   */
    @Prop() actions: ToastAction[] | string;

    /**
   * Time limit for toast in ms, if absent it will be dismissible only via close button
   */
    @Prop() duration?: number;

    /**
   * Action emitted when clicked on actions
   */
    @Event() action: EventEmitter<ToastAction>;

    @State() private _actions: ToastAction[];

    private _timer: NodeJS.Timeout;

    @Watch('type')
    typeWatcher(_: PortalToastType) {
        this._updateTimer();
    }

    @Watch('message')
    messageWatcher(_: string) {
        this._updateTimer();
    }

    @Watch('isopen')
    isopenWatcher(isOpen: boolean) {
        if (isOpen) {
            this._updateTimer();
        } else {
            clearTimeout(this._timer);
        }
    }

    @Watch('actions')
    actionsWatcher(actions: string | ToastAction[]) {
        this._actions = typeof actions === 'string' ? JSON.parse(actions) : actions;
        this._updateTimer();
    }

    componentWillLoad() {
        this.typeWatcher(this.type);
        this.messageWatcher(this.message);
        this.actionsWatcher(this.actions);
        this.isopenWatcher(this.isopen);
    }

    render() {
        const theme = getTheme(store).light ? 'light' : 'dark';
        const icon = ICON_MAP.get(this.type);
        return (
            <Host>
                <div class={`portal-toast-container ${this.isopen ? 'portal-toast-container-open' : ''}`} role="alert">
                    <div class="portal-toast-body">
                        <portal-icon
                            {...icon}
                            class={`portal-toast-icon-${this.type} ${theme}`}
                            size="30px"
                        ></portal-icon>
                        <span class="portal-toast-content">{this.message}</span>
                    </div>
                    <div class="actions">
                        {this._actions?.map(button => (
                            <div class="action"
                                tabIndex={0}
                                onClick={() => this._handleAction(button)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        this._handleAction(button);
                                    }
                                }}>
                                {button.label}
                            </div>
                        ))}
                        { !this.duration && <portal-icon
                            name="close"
                            ariaLabel={t('close', getLanguage(store))}
                            class="portal-toast-close"
                            onClick={() => this._handleClose()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    this._handleClose();
                                }
                            }}
                            tabIndex={0}
                        ></portal-icon>}
                    </div>
                </div>
            </Host>
        );
    }

    private _updateTimer() {
        clearTimeout(this._timer);
        if (this.duration) {
            this._timer = setTimeout(() => (this.isopen = false), this.duration);
        }
    }

    private _handleAction = (action: ToastAction) => {
        this.action.emit(action);
    };

    private _handleClose() {
        this.isopen = false;
    }
}
